import { createStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { ZodType } from 'zod';
import { ICodeEntry, StoreSlice } from '@/store';
import { NavigateFunction } from 'react-router';
import { EntryItemType, EntryType } from '@morph-mapper/types';

export interface CodeEntryProps {
  id: string;

  // state for entry in-row editing
  tempValue: any;

  // UI state for entry in-row editing
  isConfirm: boolean;
  isEditing: boolean;
}

export type InitCodeEntryProps = Pick<CodeEntryProps, 'id'>;

export interface CodeEntryState extends CodeEntryProps {
  // Routing
  navigate: NavigateFunction;

  getId: () => string;
  getType: () => EntryType;
  setType: (type: EntryItemType) => void;
  getName: () => string | undefined;
  getComputed: () => any;
  getValidation: () => ZodType<any> | undefined;

  // functions for entry in-row editing
  setTempValue: (tempValue: any) => void;
  setConfirm: (toggle: boolean) => void;
  setEditing: (toggle: boolean) => void;

  cancelDelete: () => void;
  confirmDelete: () => void;
  onEdit: () => void;
  onClear: () => void;
}

export type InitCodeEntryState = Pick<CodeEntryState, 'navigate'>;

export type CodeEntryStore = ReturnType<typeof createCodeEntryStore>;

export const createCodeEntryStore = (
  global: StoreSlice,
  initProps: InitCodeEntryProps & InitCodeEntryState,
) => {
  const DEFAULT_VALUES: Omit<CodeEntryProps, keyof InitCodeEntryProps> = {
    tempValue: undefined,
    isConfirm: false,
    isEditing: false,
  };

  return createStore<CodeEntryState & StoreSlice & ICodeEntry>()(
    immer((set, get) => ({
      ...global,
      ...global.entries.entry(initProps.id, EntryType.Code),
      ...DEFAULT_VALUES,
      ...initProps,

      setTempValue: (tempValue) =>
        set((s) => {
          s.tempValue = tempValue;
        }),
      setConfirm: (toggle) => {
        set((s) => {
          s.isConfirm = toggle;
        });
      },
      setEditing: (toggle) => {
        set((s) => {
          s.isEditing = toggle;
        });
      },
      cancelDelete: () => {
        set((s) => {
          s.isConfirm = false;
        });
      },
      confirmDelete: () => {
        set((s) => {
          s.clear();

          s.isConfirm = false;
          s.isEditing = false;
        });
      },
      onEdit: () => {
        get().navigate(`/${get().config.getId()}/entries/${get().id}/code`);
      },
      onClear: () => {
        set((s) => {
          s.clear();

          s.isConfirm = true;
          s.isEditing = false;
        });
      },
    })),
  );
};
